import { useCasinoDeps } from '@st/casino/useDeps'
import { isEqual } from '@st/utils'
import type { GameMode } from '../types'
import { useGameInitStore } from '../stores/useGameInitStore'

export interface UseGamePageParams {
  gameId: Ref<number> | number | (() => number)
}

export interface UseGamePageReturn {
  gamePageCrumbs: Readonly<Ref<{ label: string; link: string }[]>>
  demoToggle: Ref<boolean>
  isShowMaxWinWidget: Ref<boolean>
  frameUrl: Readonly<Ref<string | null>>
  gameMode: Ref<GameMode>
  isLoading: Readonly<Ref<boolean>>
  isShownFavoriteButton: Readonly<Ref<boolean>>
  hasDemo: Readonly<Ref<boolean>>
  handleFrameLoad: () => void
}

export function useGamePage(params: UseGamePageParams): UseGamePageReturn {
  const { isAuthenticated, activeAccount, userCurrencySettings } =
    useCasinoDeps()
  const { t } = useI18n()
  const isShowMaxWinWidget = ref(false)

  const gameId = computed(() => toValue(params.gameId))

  const gameStore = useGameInitStore()
  const { launchGame } = gameStore
  const { frameUrl, gameData } = storeToRefs(gameStore)
  const hasDemo = computed(() => gameData.value.hasDemoMode)

  const gameMode = useRouteQuery<GameMode>('mode', 'real')
  const demoToggle = computed({
    get: () => gameMode.value === 'real',
    set: (newValue: boolean) => {
      gameMode.value = newValue ? 'real' : 'demo'
    },
  })
  const isNotEnoughMoneyData = ref<{
    accountId: number
    sessionId?: string
  } | null>(null)

  const gamePageCrumbs = computed(() => [
    { label: t('breadCrumbs.main'), link: '/' },
    { label: t('breadCrumbs.casino'), link: '/casino' },
  ])

  const loading = ref<boolean>(true)

  function handleFrameLoad() {
    loading.value = false
  }

  const router = useRouter()
  const route = useRoute()
  const { goBack } = useRouterBack()

  function handleMessageRedirect(event: MessageEvent) {
    if (event.data.type === 'openedMainPage') {
      console.info(
        '%c Сообщение от iframe получено, произошел редирект на главную',
        'color: green ;font-weight:bold;',
      )
      goBack()
    }
  }

  function handleMessage(event: MessageEvent) {
    const type = event?.data?.type

    if (type === 'openedMainPage') {
      router.push('/casino')
    } else if (type === 'openedRestrictionUrl') {
      router.replace({
        query: {
          ...route.query,
          modal: 'gameGeoError',
        },
      })
    }
  }

  onBeforeMount(() => {
    window?.addEventListener('message', handleMessage)
    window?.addEventListener('message', handleMessageRedirect)
  })
  onUnmounted(() => {
    window?.removeEventListener('message', handleMessage)
    window?.removeEventListener('message', handleMessageRedirect)
  })

  const io = useSocket()

  function handleNotEnoughMoney(value: {
    accountId: number
    sessionId?: string
  }) {
    if (isEqual(isNotEnoughMoneyData.value, value)) {
      return
    }
    isNotEnoughMoneyData.value = value
    if (route.query.modal !== 'payments') {
      router.replace({ query: { ...route.query, modal: 'lowBalance' } })
      return
    }
    router.replace({ query: { ...route.query, modal: 'payments' } })
  }
  io.on('casinoNotEnoughMoney', handleNotEnoughMoney)
  onUnmounted(() => {
    io.off('casinoNotEnoughMoney', handleNotEnoughMoney)
  })

  watch(
    [
      () => activeAccount.value?.currencyId,
      () => userCurrencySettings.value.preferredCurrencyCodeForCasino,
    ],
    () => {
      launchGame({ gameId: gameId.value, gameMode: gameMode.value })
    },
  )

  return {
    gamePageCrumbs,
    demoToggle,
    isShowMaxWinWidget,
    frameUrl,
    gameMode,
    isLoading: loading,
    isShownFavoriteButton: isAuthenticated,
    hasDemo,
    handleFrameLoad,
  }
}
